import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listRegisteredSchools, getSchoolById, formatSchoolAddress } from '../constants/schools';

/**
 * SchoolSelectorField
 * -------------------
 * Searchable, alphabetical dropdown of registered schools for the school-rep
 * referral flow. Selecting a school reveals its address and records enough on
 * the answer for the backend (which has no schools registry) to persist the
 * school and tag the child as `primary` / `secondary`.
 *
 * Stored value shape (under the question's questionId):
 *   {
 *     schoolId, schoolName, schoolType,            // 'primary' | 'secondary'
 *     schoolAddress: { line1, city, postcode }
 *   }
 *
 * The `schoolType` tag drives DOB age bands (primary 4-11, secondary 11-16) and
 * whether the HFP / proceed-with-triage steps are shown (secondary skips them).
 */
export default function SchoolSelectorField({ question, value, onChange }) {
  const schools = useMemo(() => listRegisteredSchools(), []);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  // Cancel any pending "close" timer if the field unmounts, so we never call
  // setOpen on an unmounted component.
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const selected = value?.schoolId ? getSchoolById(value.schoolId) : null;

  // While a school is selected and the box is closed, show its name in the input.
  const inputText = open ? query : (selected ? selected.name : '');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter((s) => s.name.toLowerCase().includes(q));
  }, [schools, query]);

  const select = (school) => {
    onChange({
      schoolId: school.id,
      schoolName: school.name,
      schoolType: school.type,
      schoolAddress: { ...school.address },
    });
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
    setOpen(true);
  };

  return (
    <div className="text-left mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Start typing to search, then select your school
        {question?.required ? <span className="text-red-600"> *</span> : null}
      </label>

      <div className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search schools..."
          value={inputText}
          onMouseDown={() => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            // Reopen on click while already focused: selecting an option keeps
            // focus in the input (the option's mousedown is prevented), so a
            // later click fires no focus event — without this the list could
            // never be reopened by clicking.
            if (!open) {
              setOpen(true);
              setQuery('');
            }
          }}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => {
            // Cancel any close scheduled by a prior blur; otherwise that stale
            // timer fires ~120ms after this click and immediately re-closes the
            // list, forcing the user to click repeatedly before it stays open.
            if (blurTimer.current) clearTimeout(blurTimer.current);
            setOpen(true);
            setQuery('');
          }}
          onBlur={() => {
            // Delay so an outside click registers before the list closes.
            blurTimer.current = setTimeout(() => setOpen(false), 120);
          }}
          className="w-full p-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
        />

        {selected && !open && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear selected school"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          >
            ×
          </button>
        )}

        {open && (
          <ul
            role="listbox"
            className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
            onMouseDown={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                No matching schools. If your school is not listed, it is not yet registered.
              </li>
            ) : (
              filtered.map((s) => {
                const isSelected = s.id === value?.schoolId;
                return (
                  <li key={s.id} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      // Keep focus in the input so selecting never blurs it (no
                      // close-timer churn); the click still fires and selects.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => select(s)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${isSelected ? 'bg-gray-50 font-medium' : ''}`}
                    >
                      {s.name}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {selected && (
        <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
          <div className="text-xs font-medium text-gray-500 mb-1">School address</div>
          <div className="text-sm text-gray-900">{formatSchoolAddress(selected)}</div>
        </div>
      )}
    </div>
  );
}
